import type { JSX } from 'react';
import type { Darstellung, Diagrammart, Zeitraumwert, Zielampel, Zielvergleich } from '@crm/kontrakte';
import { achseVon } from '@crm/kontrakte/src/achsen';
import { type Bildoptionen, diagrammartWirksam, istDiagrammart, optionGewaehlt } from '@crm/kontrakte/src/diagrammarten';

import {
  type Abfrageergebnis,
  type Ergebniszeile,
  type Katalogkennzahl,
  type Kennzahlwert,
  tagText,
  veraenderungText,
  wertText,
} from '../api/berichte';
import {
  type Diagrammpunkt,
  type Kategorie,
  type Kreisstueck,
  type Lagewert,
  type Legendeneintrag,
  type Reihe,
  type Reihenfarbe,
  kreisstuecke,
} from '../komponenten/Diagramm';
import type { Zelle } from './Herkunft';

/**
 * Das Ergebnis eines Berichts als Diagrammreihen und als Tabelle (AUS-16).
 *
 * Die Tabelle ist die zweite Form DERSELBEN Zahlen — fuer Vorlesehilfen und
 * die Tastatur (NFA-14) und fuer jeden, der eine Zahl genau wissen will. Sie
 * steht deshalb immer unter dem Diagramm und zeigt IMMER alles: jede
 * Kennzahl, jeden Vergleich mit seiner Veraenderung (Auftrag #26, CRM-42).
 */

/**
 * Zeitraum und Vergleichszeitraeume in der Form des Ergebniskopfs — „01.05.2026
 * – 31.10.2026 · läuft noch · Vorjahr: 01.05.2025 – 01.10.2025 (bis zum selben
 * Tag)". Das Berichtswidget legt ihn als Titel auf seine knappe Zeitraumzeile
 * (Auftrag CRM-60).
 */
export function zeitraeumeText(e: Abfrageergebnis): string {
  const haupt = `${tagText(e.zeitraum.von)} – ${tagText(e.zeitraum.bis)}`;
  const stand = e.unvollstaendig.laufenderZeitraum ? 'läuft noch' : 'abgeschlossen';
  const vergleiche = e.vergleiche.map(
    (v) => `${v.name}: ${tagText(v.von)} – ${tagText(v.bis)}${v.bisZumSelbenTag ? ' (bis zum selben Tag)' : ''}`,
  );
  return [haupt, stand, ...vergleiche].join(' · ');
}

/** Nur zum Zeichnen: Die Hoehe eines Balkens braucht kein Dezimalrechnen (RND-01). */
function zahl(w: string | null): number | null {
  return w === null ? null : Number(w);
}

/** Die erste Kennzahl im Hauptzeitraum als Punkte — fuer die kleine Fassung in der Liste. */
export function punkteAus(e: Abfrageergebnis): readonly Diagrammpunkt[] {
  const k = e.kennzahlen[0]!;
  return e.zeilen.map((z) => ({
    schluessel: z.schluessel ?? '',
    beschriftung: z.bezeichnung,
    wert: zahl(z.werte[0]?.wert ?? null),
    unvollstaendig: z.unvollstaendig,
    wertText: wertText(z.werte[0]?.wert ?? null, k.einheit),
  }));
}

/**
 * WAS DAS DIAGRAMM ZEIGT (Auftrag #26, CRM-42), und warum nicht alles.
 *
 *   - die Kennzahlen der linken Achse (die Einheit der ersten, achsen.ts) in
 *     der gewaehlten Darstellung — als Balken je Gruppe nebeneinander;
 *   - die der rechten Achse als Linien mit eckigen Punkten;
 *   - die Vergleiche NUR der ersten Kennzahl (hellere Balken bzw.
 *     gestrichelte und gepunktete Linien), gleich neben ihr.
 *
 * Jede Kennzahl hat ihre Reihenfarbe nach ihrer Stelle in der Beschreibung;
 * die Legende nennt bei zwei Achsen die Achse. Die Vergleiche der uebrigen
 * Kennzahlen stehen in der Tabelle, und der Satz unter dem Bild sagt es: Mit
 * fuenf Kennzahlen und zwei Vergleichen waeren es fuenfzehn Reihen auf einer
 * Skala — das liest niemand.
 *
 * Die rechte Achse zeichnet immer Linien: Zwei Balkengruppen auf zwei
 * Skalen nebeneinander luegen — gleich hohe Balken meinen verschiedene
 * Zahlen. Eine Linie auf eigener Achse liest man als Verlauf.
 */
/**
 * Auftrag CRM-47: was die Anzeige des Berichts aus dem Bild nimmt — die
 * Vergleiche und die Ziellinie. Die Tabelle behaelt die Vergleiche immer.
 */
export type Bildteile = {
  readonly vergleiche: boolean;
  readonly ziele: boolean;
  /**
   * AUS-34 (ÄA-56, Auftrag CRM-92): welche Kennzahloptionen das Bild
   * mitzeichnet (`bildoptionen`); fehlt = keine — die kleinen Fassungen.
   */
  readonly optionen?: Bildoptionen;
};

const ALLE_TEILE: Bildteile = { vergleiche: true, ziele: true };

/** Was `reihenAus` liefert: die Reihen, ihre Legende und die Saetze unter dem Bild. */
export type Bildreihen = {
  readonly kategorien: Kategorie[];
  readonly reihen: Reihe[];
  readonly legende: Legendeneintrag[];
  readonly nurTabelle: string | null;
  readonly gestapelt: boolean;
  /** AUS-34: die Zusatzzeichen in Worten — fuer den Namen des Bilds (NFA-14). */
  readonly zeichen?: string | null;
  /** AUS-34: Die laufende Summe steht auf der sonst leeren rechten Achse — in dieser Einheit. */
  readonly rechtsKumuliert?: Katalogkennzahl | null;
};

/**
 * AUS-34 (ÄA-56): die Lage einer Zeile — was gewaehlt ist und einen Wert hat.
 * null, wo nichts davon da ist.
 */
export function lagewert(e: Abfrageergebnis, z: Ergebniszeile, i: number, k: Katalogkennzahl): Lagewert | null {
  const w = z.werte[i];
  const mm = optionGewaehlt(e, k, 'minMax');
  const med = optionGewaehlt(e, k, 'median');
  const q = med && optionGewaehlt(e, k, 'quartile');
  const min = mm ? zahl(w?.min ?? null) : null;
  const max = mm ? zahl(w?.max ?? null) : null;
  const median = med ? zahl(w?.median ?? null) : null;
  const q1 = q ? zahl(w?.q1 ?? null) : null;
  const q3 = q ? zahl(w?.q3 ?? null) : null;
  const paar = (a: number | null, b: number | null): boolean => a !== null && b !== null;
  if (!paar(min, max) && !paar(q1, q3) && median === null) return null;
  return {
    min: paar(min, max) ? min : null,
    max: paar(min, max) ? max : null,
    q1: paar(q1, q3) ? q1 : null,
    q3: paar(q1, q3) ? q3 : null,
    median,
    text: [`${z.bezeichnung}, ${k.name}`, ...lagetexte(w, k, paar(min, max), median !== null, paar(q1, q3))].join(' · '),
  };
}

function lagetexte(w: Kennzahlwert | undefined, k: Katalogkennzahl, mm: boolean, med: boolean, q: boolean): string[] {
  const t = (x: string | null | undefined): string => wertText(x ?? null, k.einheit);
  return [
    ...(mm ? [`Min ${t(w?.min)}`, `Max ${t(w?.max)}`] : []),
    ...(q ? [`Q1 ${t(w?.q1)}`] : []),
    ...(med ? [`Median ${t(w?.median)}`] : []),
    ...(q ? [`Q3 ${t(w?.q3)}`] : []),
  ];
}

/** AUS-34: was die Kurzinfo ueber den Wert hinaus nennt — Lage, Anteil, n. */
export function zusatztext(w: Kennzahlwert | undefined, lage: Lagewert | null, anteil: boolean, n: boolean): string | null {
  const teile = [
    ...(lage === null ? [] : lage.text.split(' · ').slice(1)),
    ...(anteil && w?.anteil != null ? [`Anteil ${wertText(w.anteil, 'Prozent')}`] : []),
    ...(n && w?.n != null ? [`n ${String(w.n)}`] : []),
  ];
  return teile.length === 0 ? null : teile.join(' · ');
}

/** AUS-34: die Zeichen der Lage in der Legende — an Balken und an Linien je ihr eigenes. */
export function lagelegende(reihen: readonly Pick<Reihe, 'art' | 'lagen'>[]): Legendeneintrag[] {
  const mit = (art: 'balken' | 'linie', f: (p: Lagewert) => boolean): boolean =>
    reihen.some((r) => r.art === art && (r.lagen ?? []).some((p) => p !== null && f(p)));
  const spanne = (p: Lagewert): boolean => p.min !== null;
  const kasten = (p: Lagewert): boolean => p.q1 !== null;
  const median = (p: Lagewert): boolean => p.median !== null;
  return [
    ...(mit('balken', spanne) ? [{ text: 'Linie mit Enden: kleinster bis größter Einzelwert', muster: 'spanne' as const }] : []),
    ...(mit('balken', kasten) ? [{ text: 'Kasten: mittlere Hälfte der Einzelwerte', muster: 'kasten' as const }] : []),
    ...(mit('balken', median) ? [{ text: 'Querstrich: Median', muster: 'median' as const }] : []),
    ...(mit('linie', spanne) ? [{ text: 'helles Band: kleinster bis größter Einzelwert', muster: 'band-spanne' as const }] : []),
    ...(mit('linie', kasten) ? [{ text: 'dunkles Band: mittlere Hälfte der Einzelwerte', muster: 'band-quartil' as const }] : []),
    ...(mit('linie', median) ? [{ text: 'gestrichelt: Median', muster: 'medianlinie' as const }] : []),
  ];
}

/** AUS-34: die Zusatzzeichen in Worten fuer den Namen des Bilds. */
export function zeichentext(reihen: readonly Pick<Reihe, 'art' | 'lagen' | 'anteile' | 'kumuliert'>[]): string | null {
  const teile = [
    ...lagelegende(reihen).map((l) => l.text),
    ...(reihen.some((r) => r.anteile?.some((a) => a != null) === true) ? ['Anteil als Beschriftung'] : []),
    ...(reihen.some((r) => r.kumuliert === true) ? ['laufende Summe als eigene Linie'] : []),
  ];
  return teile.length === 0 ? null : `Zusatzzeichen: ${teile.join('; ')}`;
}

/**
 * AUS-34: Die laufende Summe waechst ueber die Monate weit ueber den
 * einzelnen Wert hinaus. Ab dem Zweieinhalbfachen steht sie auf der rechten
 * Achse — wenn die frei ist; sonst auf der Achse ihrer Kennzahl.
 */
const KUMULIERT_EIGENE_ACHSE_AB = 2.5;

export function reihenAus(
  e: Abfrageergebnis,
  darstellung: 'balken' | 'gestapelt' | 'linie',
  teile: Bildteile = ALLE_TEILE,
): Bildreihen {
  const kategorien = e.kennzahlen.length === 0 ? [] : e.zeilen.map((z) => ({ schluessel: z.schluessel ?? '', beschriftung: z.bezeichnung }));
  const reihen: Reihe[] = [];
  const legende: Legendeneintrag[] = [];
  const seiten = e.kennzahlen.map((_, i) => achseVon(e.kennzahlen, i));
  const zweiAchsen = seiten.includes('rechts');
  const achsenwort = (i: number): string => (zweiAchsen ? ` (${seiten[i] === 'links' ? 'linke' : 'rechte'} Achse)` : '');
  if (e.aufteilung != null) return aufgeteilteReihen(e, darstellung, kategorien, achsenwort, teile);
  if (darstellung === 'gestapelt') darstellung = 'balken';

  const o = teile.optionen;
  const kumulierte: Reihe[] = [];
  let rechtsKumuliert: Katalogkennzahl | null = null;
  e.kennzahlen.forEach((k, i) => {
    const ton = (i + 1) as Reihenfarbe;
    const achse = seiten[i]!;
    const art = achse === 'links' ? darstellung : 'linie';
    /* AUS-34 (ÄA-56): die Optionen nur an der Hauptreihe — die Vergleiche bleiben schlicht. */
    const lagen = o?.lage === true ? e.zeilen.map((z) => lagewert(e, z, i, k)) : [];
    const anteil = o?.anteil === true && optionGewaehlt(e, k, 'anteil');
    const n = o?.n === true && (e.beschreibung.n ?? []).includes(k.kennung);
    const zusatz = e.zeilen.map((z, j) => zusatztext(z.werte[i], lagen[j] ?? null, anteil, n));
    const werte = e.zeilen.map((z) => zahl(z.werte[i]?.wert ?? null));
    reihen.push({
      name: k.name,
      achse,
      art,
      ton,
      stufe: 0,
      werte,
      texte: e.zeilen.map((z) => wertText(z.werte[i]?.wert ?? null, k.einheit)),
      unvollstaendig: e.zeilen.map((z) => z.unvollstaendig),
      ...(lagen.some((p) => p !== null) ? { lagen } : {}),
      ...(anteil ? { anteile: e.zeilen.map((z) => (z.werte[i]?.anteil == null ? null : wertText(z.werte[i]!.anteil!, 'Prozent'))) } : {}),
      ...(zusatz.some((t) => t !== null) ? { zusatz } : {}),
    });
    legende.push({ text: `${k.name}${achsenwort(i)}`, muster: art, ton, stufe: 0 });
    /* AUS-33, AUS-34: die laufende Summe „zusaetzlich" als eigene Linie ueber Balken oder Linie. */
    if (o?.kumuliert === true && optionGewaehlt(e, k, 'kumuliert')) {
      const kum = e.zeilen.map((z) => zahl(z.werte[i]?.kumuliert ?? null));
      const gross = (ws: readonly (number | null)[]): number => Math.max(0, ...ws.map((w) => Math.abs(w ?? 0)));
      const eigene = !zweiAchsen && rechtsKumuliert === null && gross(kum) > KUMULIERT_EIGENE_ACHSE_AB * gross(werte);
      if (eigene) rechtsKumuliert = k;
      const kachse = eigene ? 'rechts' : achse;
      kumulierte.push({
        name: `${k.name} kumuliert`,
        achse: kachse,
        art: 'linie',
        ton,
        stufe: 0,
        kumuliert: true,
        werte: kum,
        texte: e.zeilen.map((z) => wertText(z.werte[i]?.kumuliert ?? null, k.einheit)),
      });
      legende.push({
        text: `${k.name} kumuliert${eigene ? ' (rechte Achse)' : achsenwort(i)}`,
        muster: 'linie',
        ton,
        stufe: 0,
        kumuliert: true,
      });
    }
    if (i > 0 || !teile.vergleiche) return;
    e.vergleiche.forEach((v, j) => {
      const stufe = (j + 1) as 1 | 2;
      reihen.push({
        name: `${k.name} · ${v.name}`,
        achse,
        art,
        ton,
        stufe,
        werte: e.zeilen.map((z) => zahl(z.werte[0]?.vergleiche[j]?.wert ?? null)),
        texte: e.zeilen.map((z) => wertText(z.werte[0]?.vergleiche[j]?.wert ?? null, k.einheit)),
      });
      legende.push({ text: `${k.name} · ${v.name}`, muster: art, ton, stufe });
    });
  });
  reihen.push(...kumulierte);
  legende.push(...lagelegende(reihen));
  const erste = e.kennzahlen[0];
  /* Auftrag #29 (AUS-12): der Zielwert der ersten Kennzahl als Linie, wo Zeilen ein Ziel haben. */
  if (teile.ziele && erste !== undefined && e.zeilen.some((z) => z.werte[0]?.ziel != null)) {
    reihen.push({
      name: `Ziel ${erste.name}`,
      achse: 'links',
      art: 'linie',
      ton: 'ziel',
      stufe: 0,
      werte: e.zeilen.map((z) => zahl(z.werte[0]?.ziel?.ziel ?? null)),
      texte: e.zeilen.map((z) => wertText(z.werte[0]?.ziel?.ziel ?? null, erste.einheit)),
    });
    legende.push({ text: `Ziel ${erste.name}`, muster: 'linie', ton: 'ziel', stufe: 0 });
  }
  if (darstellung === 'balken' && e.zeilen.some((z) => z.unvollstaendig)) {
    legende.push({ text: 'noch nicht abgeschlossen', muster: 'unvollstaendig' });
  }
  const uebrige = e.kennzahlen.slice(1).map((k) => `„${k.name}"`);
  return {
    kategorien,
    reihen,
    legende,
    nurTabelle:
      e.vergleiche.length === 0
        ? null
        : !teile.vergleiche
          ? 'Vergleiche nur in der Tabelle.'
          : uebrige.length > 0
            ? `Vergleiche von ${uebrige.join(', ')} nur in der Tabelle.`
            : null,
    gestapelt: false,
    zeichen: zeichentext(reihen),
    rechtsKumuliert,
  };
}

/**
 * MIT AUFTEILUNG (Auftrag CRM-46): Auf der Achse steht die Gruppierung, je
 * Kennzahl und Wert der Aufteilung eine Reihe — „Teilnehmer · Privatkunde".
 * Die Farben laufen ueber alle Reihen (hoechstens zehn, achsen.ts); hat die
 * Aufteilung mehr Werte, als Platz ist, stehen die groessten fuer sich und
 * „Übrige" fasst den Rest zusammen, gerechnet vom Server. Die linke Achse in
 * der gewaehlten Darstellung — nebeneinander, gestapelt oder als Linien —,
 * die rechte immer als Linien. Vergleiche zeigt das Bild hier nicht: Je Wert
 * ein heller Balken daneben waeren doppelt so viele Reihen. Die Tabelle hat
 * sie. Die Ziellinie gilt der Summe und steht deshalb nur ueber dem Stapel.
 */
function aufgeteilteReihen(
  e: Abfrageergebnis,
  darstellung: 'balken' | 'gestapelt' | 'linie',
  kategorien: Kategorie[],
  achsenwort: (i: number) => string,
  teile: Bildteile,
): Bildreihen {
  const t = e.aufteilung!;
  const reihen: Reihe[] = [];
  const legende: Legendeneintrag[] = [];
  const gestapelt = darstellung === 'gestapelt';
  const mehrere = e.kennzahlen.length > 1;
  let ton = 0;
  e.kennzahlen.forEach((k, i) => {
    const achse = achseVon(e.kennzahlen, i);
    const art = achse === 'links' && darstellung !== 'linie' ? 'balken' : 'linie';
    const werte: { name: string; zelle: (z: Ergebniszeile) => (Zeitraumwert & { readonly anteil?: string | null }) | undefined; wert: (z: Ergebniszeile) => string | null }[] = [
      ...t.werte.slice(0, t.imBild).map((w, j) => ({
        name: w.bezeichnung,
        zelle: (z: Ergebniszeile) => z.teile?.[j]?.[i],
        wert: (z: Ergebniszeile) => z.teile?.[j]?.[i]?.wert ?? null,
      })),
      ...(t.imBild < t.werte.length
        ? [{ name: 'Übrige', zelle: (z: Ergebniszeile) => z.uebrige?.[i], wert: (z: Ergebniszeile) => z.uebrige?.[i]?.wert ?? null }]
        : []),
    ];
    /* AUS-34 (ÄA-56): Mit Aufteilung ist nur der Anteil eindeutig — er steht an jedem Teil. */
    const anteil = teile.optionen?.anteil === true && optionGewaehlt(e, k, 'anteil');
    for (const w of werte) {
      const anteile = e.zeilen.map((z) => {
        const a = w.zelle(z)?.anteil ?? null;
        return anteil && a !== null ? wertText(a, 'Prozent') : null;
      });
      ton = Math.min(ton + 1, 10);
      const name = mehrere ? `${k.name} · ${w.name}` : w.name;
      reihen.push({
        name,
        achse,
        art,
        ton: ton as Reihenfarbe,
        stufe: 0,
        werte: e.zeilen.map((z) => zahl(w.wert(z))),
        texte: e.zeilen.map((z) => wertText(w.wert(z), k.einheit)),
        unvollstaendig: e.zeilen.map((z) => z.unvollstaendig),
        ...(anteile.some((a) => a !== null)
          ? { anteile, zusatz: anteile.map((a) => (a === null ? null : `Anteil ${a}`)) }
          : {}),
      });
      legende.push({ text: `${name}${achsenwort(i)}`, muster: art, ton: ton as Reihenfarbe, stufe: 0 });
    }
  });
  const erste = e.kennzahlen[0];
  if (teile.ziele && gestapelt && erste !== undefined && e.zeilen.some((z) => z.werte[0]?.ziel != null)) {
    reihen.push({
      name: `Ziel ${erste.name}`,
      achse: 'links',
      art: 'linie',
      ton: 'ziel',
      stufe: 0,
      werte: e.zeilen.map((z) => zahl(z.werte[0]?.ziel?.ziel ?? null)),
      texte: e.zeilen.map((z) => wertText(z.werte[0]?.ziel?.ziel ?? null, erste.einheit)),
    });
    legende.push({ text: `Ziel ${erste.name} (Summe)`, muster: 'linie', ton: 'ziel', stufe: 0 });
  }
  if (darstellung !== 'linie' && e.zeilen.some((z) => z.unvollstaendig)) {
    legende.push({ text: 'noch nicht abgeschlossen', muster: 'unvollstaendig' });
  }
  const saetze = [
    ...(t.imBild < t.werte.length
      ? [`„Übrige" fasst ${String(t.werte.length - t.imBild)} weitere Werte nach ${t.gruppierung.name} zusammen.`]
      : []),
    ...(e.vergleiche.length > 0 ? ['Vergleiche nur in der Tabelle.'] : []),
  ];
  return { kategorien, reihen, legende, nurTabelle: saetze.length === 0 ? null : saetze.join(' '), gestapelt, zeichen: zeichentext(reihen) };
}

/**
 * TAUGT DAS ERGEBNIS FUER EINEN KREIS? (29.09.2026, zu Auftrag #26)
 *
 * Ein Kreis zeigt Anteile an einem Ganzen. Das gibt es nur, wenn
 *   - genau eine Kennzahl gewaehlt ist (Auftrag CRM-42) — mehrere Kreise
 *     nebeneinander vergleicht niemand;
 *   - nach etwas gruppiert ist, das keine Zeit ist — Monate sind kein Ganzes,
 *     sondern ein Verlauf;
 *   - die Kennzahl eine Summe oder Anzahl ist — Quoten addieren sich nicht;
 *   - kein Wert negativ ist — eine Gutschrift hat kein Kreisstueck;
 *   - ueberhaupt etwas da ist.
 * Die Antwort ist der Grund, warum nicht, oder null.
 */
export function kreisUntauglich(e: Abfrageergebnis): string | null {
  const k = e.kennzahlen[0];
  if (k === undefined) return 'Es gibt keine Kennzahl.';
  if (e.kennzahlen.length > 1) return `Ein Kreis zeigt eine Kennzahl; gewählt sind ${String(e.kennzahlen.length)}.`;
  if (e.gruppierung === null) return 'Ein Kreis braucht eine Gruppierung.';
  // Auftrag CRM-46: zwei Dimensionen sind kein Ganzes aus Stuecken — dafuer gibt es den Stapel.
  if (e.aufteilung != null) return 'Ein Kreis zeigt eine Dimension; aufgeteilt sind es zwei — dafür gibt es gestapelte Balken.';
  if (e.gruppierung.zeitlich) return 'Ein Verlauf über die Zeit ist kein Ganzes — dafür sind Balken und Linie da.';
  if (k.aggregat === 'quote') return 'Quoten addieren sich nicht zu einem Ganzen.';
  if (k.aggregat === 'durchschnitt') return 'Durchschnitte addieren sich nicht zu einem Ganzen.';
  const werte = e.zeilen.map((z) => zahl(z.werte[0]?.wert ?? null) ?? 0);
  if (werte.some((w) => w < 0)) return 'Negative Werte haben kein Kreisstück.';
  if (!werte.some((w) => w > 0)) return 'Im Zeitraum gibt es keine Werte.';
  return null;
}

/**
 * Wann es den Kreis gibt, als Satz (Auftrag #33): Er steht neben dem Grund
 * aus `kreisUntauglich`, an der gesperrten Wahl „Kreis".
 */
export const KREIS_BEDINGUNG =
  'Den Kreis gibt es mit genau einer Kennzahl, wenn nach einem Merkmal statt nach der Zeit gruppiert und nicht aufgeteilt ist, die Kennzahl eine Summe oder Anzahl ist und kein Wert negativ ist.';

/**
 * TAUGT DAS ERGEBNIS FUER GESTAPELTE BALKEN? (Auftrag CRM-46)
 *
 * Ein Stapel zeigt, woraus sich eine Summe zusammensetzt. Das gibt es nur
 * mit einer Aufteilung und nur fuer Kennzahlen, die sich addieren — Summen
 * und Anzahlen. Zwei Quoten aufeinander ergaeben eine Hoehe, die keine Quote
 * ist. Gestapelt wird die linke Achse; die rechte zeichnet Linien.
 */
export function gestapeltUntauglich(e: Abfrageergebnis): string | null {
  if (e.aufteilung == null) return 'Gestapelt werden die Werte einer Aufteilung — wählen Sie links „Aufteilen nach".';
  const nicht = e.kennzahlen.filter(
    (k, i) => achseVon(e.kennzahlen, i) === 'links' && (k.aggregat === 'quote' || k.aggregat === 'durchschnitt'),
  );
  if (nicht.length > 0) {
    return `${nicht.map((k) => `„${k.name}"`).join(', ')}: Quoten und Durchschnitte addieren sich nicht zu einer Summe.`;
  }
  return null;
}

/** Wann es gestapelte Balken gibt, als Satz — neben dem Grund an der gesperrten Wahl. */
export const GESTAPELT_BEDINGUNG =
  'Gestapelte Balken gibt es mit einer Aufteilung, wenn die Kennzahlen der linken Achse Summen oder Anzahlen sind.';

/** Die Stuecke der ersten Kennzahl im Hauptzeitraum. */
export function kreisAus(e: Abfrageergebnis): readonly Kreisstueck[] {
  const k = e.kennzahlen[0]!;
  return kreisstuecke(
    e.zeilen.map((z) => ({
      schluessel: z.schluessel ?? '',
      beschriftung: z.bezeichnung,
      wert: zahl(z.werte[0]?.wert ?? null) ?? 0,
      wertText: wertText(z.werte[0]?.wert ?? null, k.einheit),
    })),
    (summe) => wertText(summe.toFixed(k.einheit === 'EUR' || k.einheit === 'Stunden' ? 2 : 0), k.einheit),
  );
}

/**
 * TAUGT DAS ERGEBNIS FUER EINE LINIE? (Auftrag #31, #33)
 *
 * Nach Wochentag gruppiert ist kein Verlauf — sieben Tage sind kein
 * Zeitraster. Die Antwort ist der Grund, warum nicht, oder null.
 */
export function linieUntauglich(e: Abfrageergebnis): string | null {
  return e.gruppierung?.kennung === 'wochentag' ? 'Wochentage sind kein Verlauf über die Zeit.' : null;
}

/** Wann es die Linie gibt, als Satz (Auftrag #33). */
export const LINIE_BEDINGUNG = 'Die Linie gibt es bei jeder Gruppierung außer nach Wochentag.';

/**
 * Die Darstellung, die ein Ergebnis wirklich bekommt (Auftrag #31): Nach
 * Wochentag gruppiert ist kein Verlauf — sieben Tage sind kein Zeitraster.
 * Eine gewaehlte Linie wird dort zu Balken.
 */
export function wirksameDarstellung<D extends string>(d: D, e: Abfrageergebnis): D | 'balken' {
  /* AUS-41: eine Diagrammart nur, wo sie passt und ihre Optionen gewählt sind — sonst Balken. */
  if (istDiagrammart(d as Darstellung)) return diagrammartWirksam(d as Diagrammart, e) === d ? d : 'balken';
  if (d === 'gestapelt' && gestapeltUntauglich(e) !== null) return 'balken';
  return d === 'linie' && linieUntauglich(e) !== null ? 'balken' : d;
}

const KURZ = new Intl.NumberFormat('de-DE', { notation: 'compact', maximumFractionDigits: 1 });

/** Das Zeichen einer Einheit in Titeln und Beschriftungen: „€", „%", „Tage", „Std.". */
export function einheitszeichen(einheit: Katalogkennzahl['einheit']): string {
  if (einheit === 'EUR') return '€';
  if (einheit === 'Prozent') return '%';
  if (einheit === 'Stunden') return 'Std.';
  return einheit;
}

/** Die Beschriftung der Werteachse: „12 Tsd. €", „75 %", „1,2 Tsd.", „40 Std.". */
export function achsenformat(einheit: Katalogkennzahl['einheit']): (wert: number) => string {
  if (einheit === 'Prozent') return (w) => `${KURZ.format(w)} %`;
  if (einheit === 'EUR') return (w) => `${KURZ.format(w)} €`;
  if (einheit === 'Tage') return (w) => `${KURZ.format(w)} T.`;
  if (einheit === 'Stunden') return (w) => `${KURZ.format(w)} Std.`;
  return (w) => KURZ.format(w);
}

/**
 * Der Titel einer Achse: bei einer Kennzahl ihr Name mit Einheit, bei
 * mehreren die Einheit und wie viele es sind — die Namen stehen in der
 * Legende (Auftrag CRM-42).
 */
export function achsentitel(ks: readonly Katalogkennzahl[]): string {
  const k = ks[0];
  if (k === undefined) return '';
  const zeichen = einheitszeichen(k.einheit);
  return ks.length === 1 ? `${k.name} (${zeichen})` : `${zeichen} · ${String(ks.length)} Kennzahlen`;
}

/**
 * AUS-07 (Auftrag #27): Jede Zahl der Tabelle ist ein Knopf, der ihre Belege
 * zeigt — mit `beiZelle`. Ohne ihn (Musterbogen, Liste) bleibt sie Text.
 */
export function Ergebnistabelle({
  ergebnis,
  beiZelle,
  summen = true,
  ziele = true,
}: {
  readonly ergebnis: Abfrageergebnis;
  readonly beiZelle?: ((zelle: Zelle, name: string) => void) | undefined;
  /** Auftrag CRM-47: die Gesamtzeile — die Summen stehen auch im Kopf des Ergebnisses. */
  readonly summen?: boolean;
  /** Auftrag CRM-47: die Zielspalten; ohne sie zeigt auch das Bild keine Ziellinie. */
  readonly ziele?: boolean;
}): JSX.Element {
  const e = ergebnis;
  const zahlknopf = (
    text: string,
    zelle: Zelle,
    name: string,
  ): JSX.Element | string =>
    beiZelle === undefined ? (
      text
    ) : (
      <button type="button" className="ergebnistabelle__zahl" onClick={() => beiZelle(zelle, name)} aria-label={`${name}: ${text} — Belege zeigen`}>
        {text}
      </button>
    );
  const gruppe = e.gruppierung?.name ?? 'Auswahl';
  const nav = e.kennzahlen.map((_, i) => (e.gesamt.werte[i]?.navOffen ?? null) !== null);
  /* Auftrag #29: die Zielspalten je Kennzahl, sobald irgendeine Zeile ein Ziel traegt. */
  const mitZiel = e.kennzahlen.map((_, i) => ziele && [...e.zeilen, e.gesamt].some((z) => z.werte[i]?.ziel != null));
  /* Auftrag CRM-88 (ÄA-53): Anteil und kumuliert je Wertspalte, Min/Max, Median, Quartile und n zur Summe. */
  const zusatz = e.kennzahlen.map((k) => zusatzspalten(e, k));
  const lage = e.kennzahlen.map((k) => lagespalten(e, k));
  const t = e.aufteilung ?? null;
  const titel = `${e.kennzahlen.map((k) => k.name).join(' und ')} je ${gruppe}${t === null ? '' : `, aufgeteilt nach ${t.gruppierung.name}`}`;
  const spalten = (i: number): readonly Wertspalte[] => wertspalten(e, i);

  const zellen = (z: (typeof e.zeilen)[number], gesamt: boolean): JSX.Element[] =>
    e.kennzahlen.flatMap((k, i) => {
      const quote = k.aggregat === 'quote';
      const zeile: Zelle['zeile'] = gesamt ? { art: 'gesamt' } : { art: 'gruppe', schluessel: z.schluessel };
      const summe = z.werte[i];
      return [
        ...spalten(i).flatMap((s) => {
          const w = s.wert(z);
          const ort = s.spalte === undefined ? `${k.name}, ${z.bezeichnung}` : `${k.name}, ${z.bezeichnung}, ${s.titel}`;
          const mit = s.spalte === undefined ? {} : { spalte: s.spalte };
          return [
            <td key={`${k.kennung}-${s.schluessel}-w`} className={s.summe && t !== null ? 'zahl ergebnistabelle__summe' : 'zahl'}>
              {zahlknopf(wertText(w?.wert ?? null, k.einheit), { kennzahl: i, zeitraum: { art: 'haupt' }, zeile, ...mit }, ort)}
            </td>,
            ...e.vergleiche.flatMap((v, j) => [
              <td key={`${k.kennung}-${s.schluessel}-${String(j)}-w`} className="zahl">
                {zahlknopf(
                  wertText(w?.vergleiche[j]?.wert ?? null, k.einheit),
                  { kennzahl: i, zeitraum: { art: 'vergleich', stelle: j }, zeile, ...mit },
                  s.spalte === undefined
                    ? `${k.name} · ${v.name}, ${z.bezeichnung}`
                    : `${k.name} · ${v.name}, ${z.bezeichnung}, ${s.titel}`,
                )}
              </td>,
              <td key={`${k.kennung}-${s.schluessel}-${String(j)}-v`} className="zahl ergebnistabelle__veraenderung">
                {veraenderungText(w?.vergleiche[j]?.veraenderung ?? null, quote)}
                <span className="nur-vorlesen"> gegen {v.name}</span>
              </td>,
            ]),
            ...zusatz[i]!.map((x) => (
              <td key={`${k.kennung}-${s.schluessel}-${x.schluessel}`} className="zahl ergebnistabelle__option">
                {x.text(w)}
              </td>
            )),
          ];
        }),
        /* AUS-16 (Auftrag CRM-86), AUS-31, AUS-32: aus den Einzelwerten — bei Aufteilung zur Summe der Zeile. */
        ...lage[i]!.map((x) => (
          <td key={`${k.kennung}-${x.schluessel}`} className="zahl ergebnistabelle__option">
            {x.text(summe)}
          </td>
        )),
        ...(mitZiel[i] === true ? zielzellen(k, summe?.ziel ?? null) : []),
        ...(nav[i] === true
          ? [
              <td key={`${k.kennung}-n`} className="zahl">
                {summe?.navOffen ?? '—'}
              </td>,
            ]
          : []),
      ];
    });

  return (
    <div className="tabellenhuelle">
      <table className="liste__tabelle ergebnistabelle">
        <caption className="nur-vorlesen">{titel}</caption>
        <thead>
          {/* Auftrag CRM-46: In der Kreuztabelle traegt eine Kopfzeile darueber die Kennzahl ueber ihre Spalten. */}
          {t !== null && (
            <tr>
              <th scope="col" rowSpan={2}>
                {gruppe}
              </th>
              {e.kennzahlen.map((k, i) => (
                <th
                  key={k.kennung}
                  scope="colgroup"
                  colSpan={
                    spalten(i).length * (1 + 2 * e.vergleiche.length + zusatz[i]!.length)
                    + lage[i]!.length
                    + (mitZiel[i] === true ? 4 : 0)
                    + (nav[i] === true ? 1 : 0)
                  }
                  className="ergebnistabelle__gruppe"
                >
                  {k.name} nach {t.gruppierung.name}
                </th>
              ))}
            </tr>
          )}
          <tr>
            {t === null && <th scope="col">{gruppe}</th>}
            {e.kennzahlen.flatMap((k, i) => [
              ...spalten(i).flatMap((s) => [
                <th key={`${k.kennung}-${s.schluessel}-w`} scope="col" className="zahl">
                  {s.titel}
                  {kumuliertStatt(e, k) ? ' (kumuliert)' : ''}
                </th>,
                ...e.vergleiche.flatMap((v, j) => [
                  <th key={`${k.kennung}-${s.schluessel}-${String(j)}-w`} scope="col" className="zahl">
                    {s.titel} · {v.name}
                  </th>,
                  <th key={`${k.kennung}-${s.schluessel}-${String(j)}-v`} scope="col" className="zahl">
                    Veränderung{k.aggregat === 'quote' ? ' (Pkt.)' : ''}
                  </th>,
                ]),
                ...zusatz[i]!.map((x) => (
                  <th key={`${k.kennung}-${s.schluessel}-${x.schluessel}`} scope="col" className="zahl">
                    {s.titel} — {x.titel}
                  </th>
                )),
              ]),
              ...lage[i]!.map((x) => (
                <th key={`${k.kennung}-${x.schluessel}`} scope="col" className="zahl">
                  {k.name} — {x.titel}
                </th>
              )),
              ...(mitZiel[i] === true
                ? [
                    <th key={`${k.kennung}-z`} scope="col" className="zahl">
                      Ziel
                    </th>,
                    <th key={`${k.kennung}-za`} scope="col" className="zahl">
                      Abweichung
                    </th>,
                    <th key={`${k.kennung}-zp`} scope="col" className="zahl">
                      Abw. %
                    </th>,
                    <th key={`${k.kennung}-zz`} scope="col">
                      Ampel
                    </th>,
                  ]
                : []),
              ...(nav[i] === true
                ? [
                    <th key={`${k.kennung}-n`} scope="col" className="zahl">
                      Nicht übertragen
                    </th>,
                  ]
                : []),
            ])}
          </tr>
        </thead>
        <tbody>
          {e.zeilen.map((z) => (
            <tr key={z.schluessel ?? '—'}>
              <th scope="row">
                {z.bezeichnung}
                {z.unvollstaendig && (
                  <>
                    {' '}
                    <span className="marke marke--warn">läuft</span>
                  </>
                )}
              </th>
              {zellen(z, false)}
            </tr>
          ))}
        </tbody>
        {summen && (
          <tfoot>
            <tr>
              <th scope="row">Gesamt</th>
              {zellen(e.gesamt, true)}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

/** Auftrag CRM-86: Steht „Min/Max" fuer die Kennzahl in der Beschreibung — und kann sie es? */
export function minMaxAn(e: Abfrageergebnis, k: Katalogkennzahl): boolean {
  return k.minMax?.moeglich === true && (e.beschreibung.minMax ?? []).includes(k.kennung);
}

/** Auftrag CRM-88 (ÄA-53): die Optionen je Kennzahl neben Min und Max. */
type Optionsfeld = 'anteil' | 'median' | 'quartile' | 'n' | 'kumuliert';

/**
 * Steht die Option fuer die Kennzahl in der Beschreibung — und kann sie es?
 * Quartile nur mit Median (AUS-31), kumuliert nur auf einer Zeitachse (AUS-33).
 */
export function optionAn(e: Abfrageergebnis, k: Katalogkennzahl, feld: Optionsfeld): boolean {
  const moeglich = k.optionen?.[feld === 'quartile' ? 'median' : feld].moeglich === true;
  const gewaehlt = (f: Optionsfeld): boolean => (e.beschreibung[f] ?? []).includes(k.kennung);
  if (!moeglich || !gewaehlt(feld)) return false;
  if (feld === 'quartile') return gewaehlt('median');
  if (feld === 'kumuliert') return e.gruppierung?.zeitlich === true;
  return true;
}

/** AUS-33: Die laufende Summe steht statt des Werts — die Wertspalte heisst dann so. */
export function kumuliertStatt(e: Abfrageergebnis, k: Katalogkennzahl): boolean {
  return optionAn(e, k, 'kumuliert') && e.beschreibung.kumuliertAls === 'statt';
}

/** Eine Spalte einer Option: Titel nach dem Gedankenstrich, Schluessel, Text aus dem Wert. */
type Optionsspalte = {
  readonly titel: string;
  readonly schluessel: string;
  readonly text: (w: Kennzahlwert | undefined) => string;
};

/** AUS-30, AUS-33: neben jeder Wertspalte der Anteil und — zusaetzlich — die laufende Summe, auch je Vergleich. */
function zusatzspalten(e: Abfrageergebnis, k: Katalogkennzahl): readonly Optionsspalte[] {
  const neben = optionAn(e, k, 'kumuliert') && !kumuliertStatt(e, k);
  return [
    ...(optionAn(e, k, 'anteil')
      ? [{ titel: 'Anteil', schluessel: 'anteil', text: (w: Kennzahlwert | undefined) => wertText(w?.anteil ?? null, 'Prozent') }]
      : []),
    ...(neben
      ? [
          { titel: 'kumuliert', schluessel: 'kum', text: (w: Kennzahlwert | undefined) => wertText(w?.kumuliert ?? null, k.einheit) },
          ...e.vergleiche.map((v, j) => ({
            titel: `${v.name} kumuliert`,
            schluessel: `kum${String(j)}`,
            text: (w: Kennzahlwert | undefined) => wertText(w?.vergleiche[j]?.kumuliert ?? null, k.einheit),
          })),
        ]
      : []),
  ];
}

/** AUS-16, AUS-31, AUS-32: aus den Einzelwerten der Zeile — Min/Max, Median, Quartile, n. */
function lagespalten(e: Abfrageergebnis, k: Katalogkennzahl): readonly Optionsspalte[] {
  const spalte = (titel: string, schluessel: string, wert: (w: Kennzahlwert) => string | null | undefined): Optionsspalte => ({
    titel,
    schluessel,
    text: (w) => wertText(w === undefined ? null : (wert(w) ?? null), k.einheit),
  });
  return [
    ...(minMaxAn(e, k) ? [spalte('min', 'min', (w) => w.min), spalte('max', 'max', (w) => w.max)] : []),
    ...(optionAn(e, k, 'median') ? [spalte('Median', 'median', (w) => w.median)] : []),
    ...(optionAn(e, k, 'quartile') ? [spalte('Q1', 'q1', (w) => w.q1), spalte('Q3', 'q3', (w) => w.q3)] : []),
    ...(optionAn(e, k, 'n')
      ? [{ titel: 'n', schluessel: 'anz', text: (w: Kennzahlwert | undefined) => wertText(w?.n == null ? null : String(w.n), 'Anzahl') }]
      : []),
  ];
}

/** Eine Wertspalte der Tabelle: ohne Aufteilung die Kennzahl, mit Aufteilung ein Wert oder die Summe. */
type Wertspalte = {
  readonly titel: string;
  /** Eindeutig je Kennzahl — fuer die Schluessel der Zellen. */
  readonly schluessel: string;
  readonly wert: (z: Ergebniszeile) => Kennzahlwert | undefined;
  /** Die Zelle fuer die Herkunft (AUS-07); ohne Aufteilung keine. */
  readonly spalte: Zelle['spalte'];
  readonly summe: boolean;
};

/**
 * DIE KREUZTABELLE (Auftrag CRM-46): je Wert der Aufteilung eine Spalte, dann
 * die Summe der Zeile — die der Server gerechnet hat, bei Quoten aus Zaehler
 * und Nenner. Ohne Aufteilung eine Spalte mit dem Namen der Kennzahl.
 */
function wertspalten(e: Abfrageergebnis, i: number): readonly Wertspalte[] {
  const k = e.kennzahlen[i]!;
  const t = e.aufteilung ?? null;
  if (t === null) return [{ titel: k.name, schluessel: 'w', wert: (z) => z.werte[i], spalte: undefined, summe: true }];
  return [
    ...t.werte.map((w, j): Wertspalte => ({
      titel: w.bezeichnung,
      schluessel: `t${String(j)}`,
      wert: (z) => z.teile?.[j]?.[i],
      spalte: { art: 'wert', schluessel: w.schluessel },
      summe: false,
    })),
    { titel: 'Summe', schluessel: 'summe', wert: (z) => z.werte[i], spalte: { art: 'alle' }, summe: true },
  ];
}

/** Die Worte der Ampel — Farbe allein sagt einer Vorlesehilfe nichts (NFA-14). */
export const AMPELWORT: Readonly<Record<Zielampel, string>> = {
  gruen: 'erreicht',
  gelb: 'knapp',
  rot: 'verfehlt',
};

/** Die vier Zielzellen einer Kennzahl (Auftrag #29, AUS-11, AUS-12). */
function zielzellen(k: Katalogkennzahl, z: Zielvergleich | null): JSX.Element[] {
  const prozent = z?.abweichungProzent ?? null;
  return [
    <td key={`${k.kennung}-z`} className="zahl">
      {z === null ? '—' : wertText(z.ziel, k.einheit)}
      {z?.anteilig === true && <span className="marke marke--warn"> anteilig</span>}
    </td>,
    <td key={`${k.kennung}-za`} className="zahl">
      {z === null || z.abweichung === null ? '—' : `${Number(z.abweichung) > 0 ? '+' : ''}${wertText(z.abweichung, k.einheit)}`}
    </td>,
    <td key={`${k.kennung}-zp`} className="zahl">
      {prozent === null ? '—' : veraenderungText(prozent, false)}
    </td>,
    <td key={`${k.kennung}-zz`}>
      {z?.ampel == null ? '—' : <span className={`zielampel zielampel--${z.ampel}`}>{AMPELWORT[z.ampel]}</span>}
    </td>,
  ];
}
